import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, interval } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'unread' | 'read';
  user_id: number | null;
  customer_id: number | null;
  product_id: number | null;
  data: any;
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationsResponse {
  success: boolean;
  message: string;
  data: {
    notifications: Notification[];
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
    unread_count: number;
  };
}

export interface UnreadCountResponse {
  success: boolean;
  message: string;
  data: {
    unread_count: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class NotificationApiService {
  private apiUrl = environment.apiBaseUrl;
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(private http: HttpClient) {
    // Poll for unread count every 30 seconds
    interval(30000).subscribe(() => {
      this.getUnreadCount().subscribe();
    });
  }

  /**
   * Get all notifications
   */
  getNotifications(params?: {
    status?: 'unread' | 'read';
    type?: string;
    priority?: string;
    per_page?: number;
    page?: number;
  }): Observable<NotificationsResponse> {
    let httpParams = new HttpParams();
    
    if (params) {
      if (params.status) httpParams = httpParams.set('status', params.status);
      if (params.type) httpParams = httpParams.set('type', params.type);
      if (params.priority) httpParams = httpParams.set('priority', params.priority);
      if (params.per_page) httpParams = httpParams.set('per_page', params.per_page.toString());
      if (params.page) httpParams = httpParams.set('page', params.page.toString());
    }

    return this.http.get<NotificationsResponse>(`${this.apiUrl}/notifications`, { params: httpParams });
  }

  /**
   * Get unread count
   */
  getUnreadCount(): Observable<UnreadCountResponse> {
    return this.http.get<UnreadCountResponse>(`${this.apiUrl}/notifications/unread-count`);
  }

  /**
   * Get a specific notification
   */
  getNotification(id: number): Observable<{success: boolean, message: string, data: Notification}> {
    return this.http.get<{success: boolean, message: string, data: Notification}>(`${this.apiUrl}/notifications/${id}`);
  }

  /**
   * Mark notification as read
   */
  markAsRead(id: number): Observable<{success: boolean, message: string, data: Notification}> {
    return this.http.put<{success: boolean, message: string, data: Notification}>(`${this.apiUrl}/notifications/${id}/read`, {});
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<{success: boolean, message: string, data: {updated_count: number}}> {
    return this.http.put<{success: boolean, message: string, data: {updated_count: number}}>(`${this.apiUrl}/notifications/mark-all-read`, {});
  }

  /**
   * Delete a notification
   */
  deleteNotification(id: number): Observable<{success: boolean, message: string}> {
    return this.http.delete<{success: boolean, message: string}>(`${this.apiUrl}/notifications/${id}`);
  }

  /**
   * Clear all notifications
   */
  clearAll(): Observable<{success: boolean, message: string, data: {deleted_count: number}}> {
    return this.http.delete<{success: boolean, message: string, data: {deleted_count: number}}>(`${this.apiUrl}/notifications/clear-all`);
  }

  /**
   * Update unread count in subject
   */
  updateUnreadCount(count: number): void {
    this.unreadCountSubject.next(count);
  }
}

